import { Box, FormControl, InputLabel, TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import RegularButton from 'components/CustomButtons/Button'
import { useForm } from 'hooks/useForm'
import React, { memo, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  createNewComoData,
  editComoData,
} from 'redux/actions/comoData/comoDataActions'
import Datetime from 'react-datetime'
import dayjs from 'dayjs'
import AttachmentIcon from "@material-ui/icons/Attachment"
import DeleteIcon from '@material-ui/icons/Delete'
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import { getCustomers, getTanks } from './getOptions'
import { AutocompleteComponent } from 'components/Autocomplete/AutocompleteComponent'
import { ModalV2 } from 'components/Modal/ModalV2'
import { Download } from '@mui/icons-material'
import { tanksColumns } from './columns'
import CardTable from 'components/CardTable/CardTable'
import { LoadingComponent } from 'components/Loading/LoadingComponent'
import { useSearch } from 'hooks/useSearch'

const blue = '#0055aa'
const MAX_FILE_SIZE = 10000000

export const FormComoDataEdit = memo((props) => {
  const { handleClose } = props
  const { station: stationSelected, comoDataSelected, addMode } = useSelector(state => state.comoData)
  const { brandsTank } = useSelector(({ comoDataTanks }) => comoDataTanks)
  const { stationsList } = useSelector(state => state.companies);
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const fileContractInputRef = useRef();
  const fileEvidenceInputRef = useRef();
  const [customersOptions, setCustomersOptions] = useState([]);
  const [tanksOptions, setTanksOptions] = useState([]);
  const [tanks, setTanks] = useState([]);
  const [customersLoadingOptions, setCustomersLoadingOptions] = useState(false);
  const [accountsOptions, setAccountsOptions] = useState([]);
  const [contract, setContract] = useState(null);
  const [evidence, setEvidence] = useState(null);
  const [deletedEvidenceIds, setDeletedEvidenceIds] = useState([]);
  const [deleteTank, setDeleteTank] = useState(false);
  const [isDownload, setIsDownload] = useState(false);
  const [isDeletedDocument, setIsDeletedDocument] = useState(false);
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [pageSize, changePageSize] = useState(5);
  const [isLoading, setIsLoading] = useState(addMode ? false : true);
  const { setSnackbar } = useSearch();
  const { inputValues, handleInputOnChange, handleNewValues } = useForm({
    kgPerMonth: '',
    totalInversion: '',
    station: '',
    customerFilter: '',
    customer: '',
    account: '',
    tank: ''
  })
  const dispatch = useDispatch()
  const { kgPerMonth, totalInversion, station, customerFilter, customer, account, tank } = inputValues

  const handleContractButtonClick = () => fileContractInputRef.current.click();
  const handleEvidenceButtonClick = () => fileEvidenceInputRef.current.click();

  const handleDeleteDocument = () => {
    if (isDeletedDocument === 'contract') {
      setContract(null)
    }
    if (isDeletedDocument === 'evidence') {
      setEvidence(prev => prev.filter(file => file.id !== selectedEvidence))
      setDeletedEvidenceIds(prev => [...prev, selectedEvidence])
    }
    setIsDeletedDocument(false)
  }

  const handleFileChange = (data) => {
    if (data) {
      if (data.size >= MAX_FILE_SIZE) {
        setSnackbar("El archivo no puede pesar mas de 10Mb", 'warning')
        return;
      }
      if (data.name.includes(".pdf")) {
        setContract(data);
        return;
      } else {
        setSnackbar("Solo se permiten archivos .pdf", 'warning')
        return;
      }
    }
  };

  const handleFilesChange = (files) => {
    if (files) {
      const newEvidences = []
      Object.keys(files).map(file => {
        if (files[file].size >= MAX_FILE_SIZE) {
          setSnackbar("El archivo no puede pesar mas de 10Mb", 'warning')
          return;
        }
        newEvidences.push(files[file])
      })
      const oldEvidences = evidence ? evidence : []
      setEvidence([...oldEvidences, ...newEvidences])
    }
  };

  const handleSearchCustomers = async (e) => {
    e.preventDefault()
    e.stopPropagation()

    if (!station) {
      setSnackbar("Selecciona una Planta", 'warning')
      return;
    }
    if (customerFilter.length === 0) {
      setSnackbar("Por favor, introduce un nombre o número de teléfono.", 'warning')
      return;
    }
    if (customerFilter.length < 4 || customerFilter.length > 250) {
      setSnackbar("El nombre debe de tener una longitud minima de 4 caracteres", 'warning')
      return;
    }
    setCustomersLoadingOptions(true)
    handleNewValues({ customer: '', account: '' })
    setAccountsOptions([])
    const customers = await getCustomers(setSnackbar, station.id, customerFilter)
    setCustomersOptions(customers ? customers : [])
    setCustomersLoadingOptions(false)
  }

  const handleSubmit = () => {

    if (startDate > endDate) {
      setSnackbar("Fecha de inicio es mayor a la de expiración", 'warning')
      return
    }

    if (!contract?.name) {
      setSnackbar("El contrato es necesario", 'warning')
      return
    }

    const newValues = {
      ...comoDataSelected,
      kgPerMonth,
      totalInversion,
      startDate,
      endDate,
      idStation: station.id,
      idCustomer: customer.id,
      idAccount: account?.id ?? null,
      tanks: tanks.map(tank => tank.id)
    }

    if (kgPerMonth > 0 && totalInversion > 0 && startDate.length > 0 && endDate.length > 0 && station && customer) {
      if (addMode) {
        dispatch(createNewComoData(newValues, evidence, contract, handleClose))
      } else {
        dispatch(editComoData(comoDataSelected, newValues, contract, evidence, deletedEvidenceIds, handleClose))
      }
    } else {
      setSnackbar("Campos con * son requeridos", 'warning')
    }
  }

  useEffect(() => {
    if (customer?.id && addMode) {
      setAccountsOptions(customer.accounts ?? [])
      handleNewValues({ account: '' })
    } else if (!customer?.id) {
      setAccountsOptions([])
    }
    // eslint-disable-next-line
  }, [customer?.id])

  useEffect(() => {
    if (deleteTank) {
      const newTanks = tanks.filter(tankObj => tankObj.id !== deleteTank.id)
      setTanks(newTanks)
      setTanksOptions([...tanksOptions, deleteTank])
      setDeleteTank(false)
    }
  }, [deleteTank])

  useEffect(() => {
    if (comoDataSelected && !station && !addMode) {
      const fillComoDataInfo = async () => {
        setTanksOptions(await getTanks(setSnackbar, comoDataSelected.idStation))
        setCustomersLoadingOptions(true)
        let customers = await getCustomers(setSnackbar, comoDataSelected.idStation, comoDataSelected.customer.name)
        customers = customers ? customers : []
        setCustomersOptions(customers)
        const matchedCustomer = customers.find(obj => obj.id === comoDataSelected.customer.id)
        const accountsFromCustomer = matchedCustomer?.accounts ?? []
        setAccountsOptions(accountsFromCustomer)
        setCustomersLoadingOptions(false)
        handleNewValues({
          station: stationsList.filter(obj => obj.id === comoDataSelected.idStation)[0],
          totalInversion: comoDataSelected.totalInversion,
          kgPerMonth: comoDataSelected.kgPerMonth,
          customer: matchedCustomer,
          customerFilter: comoDataSelected.customer.name,
          account: accountsFromCustomer.find(a => a.id === comoDataSelected.account?.id)
            ?? (comoDataSelected.account ? { id: comoDataSelected.account.id, value: comoDataSelected.account.id, label: comoDataSelected.account.code } : '')
        })
        setContract(comoDataSelected.contract)
        setEvidence(comoDataSelected.evidence)
        setStartDate(comoDataSelected.startDate)
        setEndDate(comoDataSelected.endDate)
        setTanks(comoDataSelected.tanks.map(tank => ({ ...tank, setDeleteTank })))
        setIsLoading(false)
      };

      fillComoDataInfo()
    } else if ((addMode || !comoDataSelected) && (stationSelected?.id !== station?.id)) {
      const fillComoDataInfo = async () => {
        const actualStation = stationSelected?.id ? stationSelected : station
        if (actualStation) setTanksOptions(await getTanks(setSnackbar, actualStation.id))
        setCustomersOptions([])
        handleNewValues({
          station: actualStation,
          customerFilter: '',
          customer: ''
        })
        setIsLoading(false)
      };

      fillComoDataInfo()
    }
    // eslint-disable-next-line 
  }, [comoDataSelected, stationSelected, station, brandsTank])

  useEffect(() => {
    let timeout;
    if (isDownload) {
      timeout = setTimeout(() => setIsDownload(false), 5000);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [isDownload]);

  useEffect(() => {
    if (tank?.id) {
      setTanks([...tanks, tank].map(tank => ({ ...tank, setDeleteTank })))
      setTanksOptions(tanksOptions.filter(tankOpt => tankOpt.id !== tank.id))
      handleNewValues({
        tank: ''
      })
    }
    // eslint-disable-next-line
  }, [tank])

  return (
    <>
      {
        !isLoading ? (
          <FormControl fullWidth style={{ display: 'flex', gap: 10, marginTop: 20 }} onSubmit={handleSubmit}>
            <FormControl fullWidth variant="outlined">
              <AutocompleteComponent
                name="station"
                value={station}
                options={stationsList}
                label="Planta *"
                handleValues={handleNewValues}
                disabled={(!addMode) || (stationSelected?.id && !comoDataSelected?.idStation)}
              />
            </FormControl>
            <form onSubmit={handleSearchCustomers} style={{ position: 'relative' }}>
              <Autocomplete
                id="como-data-customers-options"
                options={customersOptions}
                value={customer || null}
                inputValue={customerFilter}
                getOptionLabel={(option) => option.label || option.name || ""}
                renderInput={(params) => (
                  <>
                    <TextField
                      {...params}
                      label={customersLoadingOptions ? "Cargando..." : "Cliente *"}
                      variant="outlined"
                      name="customerFilter"
                      onChange={handleInputOnChange}
                    />
                    <Box style={{ position: "absolute", right: 8, top: 8, display: "flex" }}>
                      <RegularButton
                        justIcon
                        round
                        color="transparent"
                        onClick={handleSearchCustomers}
                        size="sm"
                        loading={customersLoadingOptions}
                        disabled={!addMode}
                      >
                        <SearchIcon style={{ color: blue }} />
                      </RegularButton>
                      <RegularButton
                        justIcon
                        round
                        color="transparent"
                        onClick={() => handleNewValues({ customerFilter: '', customer: '' })}
                        size="sm"
                        tooltip="Limpiar"
                        disabled={!addMode || customersLoadingOptions}
                      >
                        <CloseIcon />
                      </RegularButton>
                    </Box>
                  </>
                )}
                disabled={!addMode || customersLoadingOptions}
                fullWidth
                onChange={(_, v) => handleNewValues({ customer: v, customerFilter: v?.label || v?.name || '' })}
                onInputChange={(_, value, reason) => {
                  if (reason === 'input') handleNewValues({ customerFilter: value })
                }}
                noOptionsText="No se encuentra el Cliente"
                style={{ position: "relative" }}
                disableClearable
                forcePopupIcon={false}
              />
            </form>
            <FormControl fullWidth variant="outlined">
              <AutocompleteComponent
                name="account"
                value={account}
                options={accountsOptions}
                label="Cuenta del cliente"
                handleValues={handleNewValues}
                disabled={!customer?.id || (!addMode && !!comoDataSelected?.account?.id)}
              />
            </FormControl>
            <FormControl fullWidth variant="outlined">
              <AutocompleteComponent
                name="tank"
                value={tank}
                options={tanksOptions}
                label="Agregar Tanques"
                handleValues={handleNewValues}
              />
            </FormControl>
            <CardTable
              data={tanks}
              columns={tanksColumns}
              pageSize={pageSize}
              onPageSizeChange={e => changePageSize(e)}
              blockBtn
            />
            <TextField
              type="number"
              label="Kilos por mes *"
              name="kgPerMonth"
              value={kgPerMonth}
              onChange={(e) => {
                if (/^\d*$/.test(e.target.value)) handleInputOnChange(e)
              }}
              onKeyDown={(e) => ['.',',','-','e','E','+'].includes(e.key) && e.preventDefault()}
              variant="outlined"
              inputProps={{ min: 1, step: 1 }}
            />
            <TextField
              type="number"
              label="Inversión *"
              name="totalInversion"
              value={totalInversion}
              onChange={(e) => {
                if (/^\d*$/.test(e.target.value)) handleInputOnChange(e)
              }}
              onKeyDown={(e) => ['.',',','-','e','E','+'].includes(e.key) && e.preventDefault()}
              variant="outlined"
              inputProps={{ min: 1, step: 1 }}
            />
            <FormControl fullWidth style={{ marginTop: 40 }} variant="outlined" onKeyDown={(e) => { e.preventDefault() }}>
              <InputLabel style={{ marginTop: -38 }} >Fecha de inicio *</InputLabel>
              {
                <Datetime
                  name="startDate"
                  dateFormat="YYYY-MM-DD"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(dayjs(e._d).format("YYYY-MM-DD"))
                  }}
                  timeFormat={false}
                />
              }
            </FormControl>
            <FormControl fullWidth style={{ marginTop: 40 }} variant="outlined" onKeyDown={(e) => { e.preventDefault() }}>
              <InputLabel style={{ marginTop: -38 }}>Fecha de expiración *</InputLabel>
              {
                <Datetime
                  name="endDate"
                  dateFormat="YYYY-MM-DD"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(dayjs(e._d).format("YYYY-MM-DD"))
                  }}
                  timeFormat={false}
                />
              }
            </FormControl>

            <FormControl style={{ display: 'flex', justifyContent: 'space-between' }} fullWidth variant="outlined">
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                padding="8px 12px"
                border="1px solid rgba(0,0,0,0.23)"
                borderRadius="4px"
              >
                <InputLabel
                  shrink
                  style={{
                    position: 'static',
                    transform: 'none',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '70%',
                  }}
                >
                  {contract?.name ? contract.name : 'Contrato *'}
                </InputLabel>
                <Box display="flex" gap={1}>
                  {contract?.name
                    ? <Box style={{ alignSelf: 'end' }}>
                      {
                        contract?.file && <RegularButton
                          color="info"
                          justIcon
                          round
                          size="small"
                          tooltip="Descargar Contrato"
                          href={contract?.file}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setIsDownload(true)}
                          loading={isDownload}
                        >
                          <Download style={{ margin: 0 }} />
                        </RegularButton>
                      }
                      <RegularButton
                        color="danger"
                        justIcon
                        round
                        size="small"
                        tooltip="Eliminar PDF"
                        onClick={() => {
                          if (comoDataSelected && comoDataSelected.contract) setIsDeletedDocument('contract')
                          else setContract(null)
                        }}
                      >
                        <DeleteIcon style={{ margin: 0 }} />
                      </RegularButton>
                    </Box>
                    : <RegularButton
                      color="info"
                      justIcon
                      round
                      size="small"
                      tooltip="Adjuntar PDF"
                      onClick={handleContractButtonClick}
                      style={{ alignSelf: 'end' }}
                    >
                      <AttachmentIcon style={{ margin: 0 }} />
                      <input
                        type="file"
                        style={{ display: "none" }}
                        ref={fileContractInputRef}
                        onChange={(e) => handleFileChange(e.target.files[0])}
                      />
                    </RegularButton>
                  }
                </Box>
              </Box>
            </FormControl>

            <FormControl fullWidth style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }} variant="outlined">
              <InputLabel shrink style={{ position: 'static', transform: 'none' }}>Evidencias</InputLabel>
              <RegularButton
                color="info"
                justIcon
                round
                size="small"
                tooltip="Adjuntar Archivos"
                onClick={handleEvidenceButtonClick}
                style={{ alignSelf: 'end' }}
              >
                <AttachmentIcon style={{ margin: 0 }} />
                <input
                  type="file"
                  multiple="multiple"
                  style={{ display: "none" }}
                  ref={fileEvidenceInputRef}
                  onChange={(e) => handleFilesChange(e.target.files)}
                />
              </RegularButton>
            </FormControl>
            {
              evidence && evidence.map(evidenceObj => (
                <FormControl style={{ width: '96%', alignSelf: 'center' }} variant="outlined">
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    padding="8px 12px"
                    border="1px solid rgba(0,0,0,0.23)"
                    borderRadius="4px"
                  >
                    <InputLabel
                      shrink
                      style={{
                        position: 'static',
                        transform: 'none',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        maxWidth: '70%',
                      }}
                    >
                      {evidenceObj.name}
                    </InputLabel>
                    <Box display="flex" gap={1}>
                      {
                        evidenceObj?.file && <RegularButton
                          color="info"
                          justIcon
                          round
                          size="small"
                          tooltip="Descargar Evidencia"
                          href={evidenceObj?.file}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => setIsDownload(true)}
                          loading={isDownload}
                        >
                          <Download style={{ margin: 0 }} />
                        </RegularButton>
                      }
                      <RegularButton
                        color="danger"
                        justIcon
                        round
                        size="small"
                        tooltip="Eliminar Archivo"
                        onClick={() => {
                          if (comoDataSelected && evidenceObj?.id) {
                            setIsDeletedDocument('evidence')
                            setSelectedEvidence(evidenceObj.id)
                          } else {
                            setEvidence(evidence.filter(file => file.name !== evidenceObj.name && file.size !== evidenceObj.size))
                            if (fileEvidenceInputRef.current) {
                              fileEvidenceInputRef.current.value = '';
                            }
                          }
                        }}
                      >
                        <DeleteIcon style={{ margin: 0 }} />
                      </RegularButton>
                    </Box>
                  </Box>
                </FormControl>
              ))
            }
            <Box sx={{ display: 'flex', justifyContent: 'space-between', marginTop: 30 }}>
              <RegularButton variant="contained" color="danger" onClick={handleClose}>Cancelar</RegularButton>
              <RegularButton
                variant="contained"
                color="facebook"
                style={{ backgroundColor: blue, color: 'white' }}
                onClick={handleSubmit}
              >
                {addMode ? 'Agregar' : 'Guardar'}
              </RegularButton>
            </Box>
          </FormControl>
        )
          : <LoadingComponent label="Cargando datos" />
      }
      <ModalV2
        open={isDeletedDocument ? true : false}
        onClose={() => setIsDeletedDocument(false)}
        title={`Seguro que desea eliminar el archivo?`}
        justConfirm
        minHeight={1}
      >
        <Box style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          gap: 4
        }}>
          <RegularButton
            color="facebook"
            onClick={() => setIsDeletedDocument(false)}
          >
            Cancelar
          </RegularButton>
          <RegularButton
            color="danger"
            onClick={handleDeleteDocument}
          >
            Eliminar
          </RegularButton>
        </Box>
      </ModalV2>
    </>
  )
})
